import { define } from 'wicked-elements'
import svg from 'svg'
import randomItem from 'random-item'

/**
 * Draws SVG with various corner shapes
 * as the background of an element
 *
 * Notes:
 * - Bevel, Curve, Sweep, Notch, false (normal corner)
 * - Can pass % radius
 * - Can pass min radius
 * - data-stroke is a boolean attribute:
 *   - We shrink 1px around egde of svg to support 1px stroke
 *
 * Enhancements:
 * - Calc inset based on stroke width for variable stroke
 */

define('[data-component~="corner-shape"]', {
  init() {
    this.radius = parseFloat(this.element.dataset.radius) || 50
    this.minRadius = parseInt(this.element.dataset.minRadius) || 0
    this.percentage = this.element.dataset?.radius?.includes('%')
    this.stroke = this.element.hasAttribute('data-stroke')

    const shapes = [
      ['bevel', false, 'curve', false],
      [false, 'curve', false, 'curve'],
      [false, 'curve', 'bevel', false],
      ['bevel', 'bevel', 'bevel', 'bevel'],
      ['bevel', 'bevel', false, 'curve'],
      [false, 'bevel', 'curve', 'bevel'],
    ]

    // Get corner from option or select random
    try {
      let parsed = JSON.parse(this.element.dataset.corners)
      parsed = parsed.map((item) =>
        item === 'false' ? false : item === 'true' ? true : item
      )
      this.corners = parsed.length === 4 ? parsed : randomItem(shapes)
    } catch (e) {
      this.corners = randomItem(shapes)
    }

    // SVG init
    this.$svg = this.stroke
      ? svg(
          `<svg preserveAspectRatio="none"><g transform="translate(0.5,0.5)"><path /></g></svg>`
        )
      : svg(`<svg preserveAspectRatio="none"><g><path /></g></svg>`)
    this.$g = this.$svg.querySelector('g')
    this.$path = this.$svg.querySelector('path')
    this.element.appendChild(this.$svg)

    this.render = this.render.bind(this)
    this.resizeObserver = new ResizeObserver(this.render)
  },
  connected() {
    this.render()
    this.resizeObserver.observe(this.element)
  },
  disconnected() {
    this.resizeObserver.unobserve(this.element)
  },
  drawCorner(corner, shape, w, h, r, d) {
    if (!shape) {
      switch (corner) {
        case 0:
          d.push('h', r, 'v', r)
          break
        case 1:
          d.push('v', r, 'h', -r)
          break
        case 2:
          d.push('h', -r, 'v', -r)
          break
        case 3:
          d.push('v', -r, 'h', r)
      }
    } else if (shape === 'notch') {
      switch (corner) {
        case 0:
          d.push('v', r, 'h', r)
          break
        case 1:
          d.push('h', -r, 'v', r)
          break
        case 2:
          d.push('v', -r, 'h', -r)
          break
        case 3:
          d.push('h', r, 'v', -r)
      }
    } else {
      if (shape === 'curve' || shape === 'scoop') {
        const sweep = +(shape === 'curve')
        d.push('a', r, r, '0', 0, sweep)
      } else if (shape === 'bevel') {
        d.push('l')
      }

      d.push(corner === 1 || corner === 2 ? -r : r)
      d.push(corner === 2 || corner === 3 ? -r : r)
    }
  },
  adjustedRadius(r, w, h) {
    // Calculate radius based on percentage
    let radius = r
    if (this.percentage) {
      const calculatedRadius = Math.max(w, h) * (r / 100)
      radius = Math.max(calculatedRadius, this.minRadius)
    }

    // Make sure radius is no greater than 1/3 of the largest size
    if (radius > w / 3 || radius > h / 3) {
      return Math.min(w, h) / 3
    } else {
      return radius
    }
  },
  render() {
    const inset = this.stroke ? 2 : 0 // space for stroke
    const w = this.element.offsetWidth
    const h = this.element.offsetHeight
    const r = this.adjustedRadius(this.radius, w, h)

    // Set svg size
    this.$svg.setAttribute('width', w + inset)
    this.$svg.setAttribute('height', h + inset)
    this.$svg.setAttribute('viewBox', `0 0 ${w + inset} ${h + inset}`)

    // Open shape
    let d = ['M', r, '0']

    // Draw corners
    this.corners.forEach((shape, corner) => {
      switch (corner) {
        case 0:
          d.push('h', w - r * 2)
          break
        case 1:
          d.push('v', h - r * 2)
          break
        case 2:
          d.push('h', -w + r * 2)
          break
        case 3:
          d.push('v', -h + r * 2)
      }

      this.drawCorner(corner, shape, w, h, r, d)
    })

    // Close shape
    d.push('Z')

    this.$path.setAttribute('d', d.join(' '))
  },
})
